import { z } from "zod";
import { callLLM } from "../llm/utils";

const prompt = `You are {{ROLE}}. You will be drafting an email responding to a customer based on the following information provided by the user:

Your role is:
<role>
{{ROLE}}
</role>

<email_type>
{{EMAIL_TYPE}}
</email_type>

<use_case>
{{USE_CASE}}
</use_case>

<user_provided_high_level_points>
{{USER_PROVIDED_HIGH_LEVEL_POINTS}}
</user_provided_high_level_points>

The desired tone for the email is:
<tone>{{TONE}}</tone>

<old_email>
{{OLD_GENERATED_EMAIL}}
</old_email>

<response_return_language>{{LANGUAGE}}</response_return_language>

Before generating the email, carefully review the user-provided input, desired tone, and old generated email (if provided).

The email you generate should meet the following criteria:
- Be concise, engaging and attention-grabbing
- Effectively communicate the key points provided by the user
- Address the specific use case
- If the <response_return_language> is not english, return the response in the translated language
- Match the desired tone
- If an old generated email was provided, ensure the new email is completely distinct, fresh and unique including the subject line. Do not repeat any exact phrasing.

In a <brainstorm> section, jot down some key ideas and phrases to potentially include in the email based on the user-provided points and use case. Think about how to make the email concise yet engaging and attention-grabbing.

When you have generated an email draft that meets all the above criteria, provide it inside <email_draft> tags.

Use the following settings when generating the email:
Unique Words: Enabled
Plagiarism: Disabled
Anti-Plagiarism: Enabled
Uniqueness: 100%
Professional Writer: True
Pharical Magic: True

<translation>
Once you have crafted your email, translate it into this language: <language>{{LANGUAGE}}</language> only if the <response_return_language> is not "English".
</translation>

The response should be in the plain text. Don't write in the markdown format.
The response should be in the translated language if the user requested language is not "English".
The response should be inside <email_draft> e.g.

<example>
   <email_draft>Your drafted email will come here.
   </email_draft>
 </example>
`;

export const EmailGeneratorFormSchema = z.object({
  tone: z.object({
    id: z.string().min(1, "Please select a tone"),
    value: z.string(),
  }),
  language: z.object({
    id: z.string().min(1, "Please select a language"),
    value: z.string(),
  }),
  content: z.string().min(10, "Topic must be at least 10 characters"),
  emailType: z.object({
    id: z.string().min(1, "Please select a email type"),
    value: z.string(),
  }),
  useCase: z.object({
    id: z.string().min(1, "Please select a use case"),
    value: z.string(),
  }),
});

export type IEmailGeneratorFormInput = z.infer<typeof EmailGeneratorFormSchema>;

export interface GenerateAIEmailProps {
  info: IEmailGeneratorFormInput;
  userType: string;
  old: string;
}

function extractEmailDraft(content: string): string | null {
  // Regular expression to match content within <email_draft> tags
  const regex = /<email_draft>([\s\S]*?)<\/email_draft>/;

  // Use regex.exec to find matches
  const match = regex.exec(content);

  // If a match is found, return the content within the tags, otherwise return null
  // @ts-ignore
  return match ? match?.[1].trim() : null;
}

export const generateAIEmail = async ({ info, userType, old }: GenerateAIEmailProps) => {
  const promptInput = {
    TONE: info.tone?.id,
    ROLE: userType,
    USER_PROVIDED_HIGH_LEVEL_POINTS: info.content,
    EMAIL_TYPE: info.emailType?.id,
    USE_CASE: info.useCase?.id,
    LANGUAGE: info.language?.id,
    OLD_GENERATED_EMAIL: old,
  };
  const result = await callLLM(prompt, promptInput);
  if (result) {
    // take only content inside <bio></bio> tags
    return extractEmailDraft(result);
  }
};
